import { ILoanSummaryDashboardOutstandingLoanTypeSummary } from '@apply/api/src/api/loanApi';
import { TabularGrid } from '@apply/components';
import React from 'react';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { Toolbar } from './components/Toolbar/Toolbar';
import { getDashboardLoanTypeName } from '../../../constants/getDashboardLoanTypeName';
import { DashboardOutstandingByMonthPivotMode } from '../../../constants/DashboardOutstandingByMonthPivotMode';

export const DashboardOutstandingByMonth: React.FC<{
    pivotMode: DashboardOutstandingByMonthPivotMode;
    set_pivotMode: (mode: DashboardOutstandingByMonthPivotMode) => void;
    dataSource?: ILoanSummaryDashboardOutstandingLoanTypeSummary[];
}> = ({ pivotMode, set_pivotMode, dataSource = [] }) => {
    const tabularColumns: string[] = ['loanTypeId', 'committedAmount', 'fundedAmount', 'number'];

    const columnValueFormat = pivotMode !== DashboardOutstandingByMonthPivotMode.Number ? 'currency' : undefined;

    return (
        <div
            id='dashboardOutstandingByMonthGrid'
            className='loanSummaryDashboard dashboardOutstandingByMonthGrid'
        >
            <Toolbar
                pivotMode={pivotMode}
                set_pivotMode={set_pivotMode}
            />

            <TabularGrid
                dataSource={dataSource}
                keyExpr='loanTypeId'
                defaultPageSize={10}
                pagerVisible={false}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                remoteOperations={false}
                tabularColumns={tabularColumns}
                noDataText='No Items Found'
            >
                <Column
                    dataField='loanTypeId'
                    caption=''
                    width={140}
                    alignment='left'
                    cellRender={(e) => getDashboardLoanTypeName(e.data.loanTypeId)}
                />
                <Column
                    dataField='committedAmount'
                    caption='Committed'
                    format='currency'
                    alignment='left'
                    width={120}
                />
                <Column
                    dataField='fundedAmount'
                    caption='Funded'
                    format='currency'
                    alignment='left'
                    width={120}
                />
                <Column
                    dataField='number'
                    caption='Number'
                    alignment='left'
                    width={120}
                />
                <Column
                    caption='MTD'
                    key='MTD'
                    dataField='MTD'
                    calculateCellValue={(row) => {
                        switch (pivotMode) {
                            case DashboardOutstandingByMonthPivotMode.Committed:
                                return row.monthToDate.committedAmount;
                            case DashboardOutstandingByMonthPivotMode.Funded:
                                return row.monthToDate.fundedAmount;
                            case DashboardOutstandingByMonthPivotMode.Number:
                                return row.monthToDate.number;
                            default:
                                return null;
                        }
                    }}
                    format={columnValueFormat}
                    alignment='left'
                    width={120}
                />

                {dataSource[0]?.monthlyDetails.map((m, idx) => (
                    <Column
                        key={m.month}
                        caption={m.month}
                        dataField={m.month}
                        width={100}
                        alignment='left'
                        format={columnValueFormat}
                        calculateCellValue={(row) => {
                            switch (pivotMode) {
                                case DashboardOutstandingByMonthPivotMode.Committed:
                                    return row.monthlyDetails[idx].counts.committedAmount;
                                case DashboardOutstandingByMonthPivotMode.Funded:
                                    return row.monthlyDetails[idx].counts.fundedAmount;
                                case DashboardOutstandingByMonthPivotMode.Number:
                                    return row.monthlyDetails[idx].counts.number;
                                default:
                                    return null;
                            }
                        }}
                    />
                ))}
                <Column
                    caption='Older'
                    key='Older'
                    dataField='Older'
                    calculateCellValue={(row) => {
                        switch (pivotMode) {
                            case DashboardOutstandingByMonthPivotMode.Committed:
                                return row.older.committedAmount;
                            case DashboardOutstandingByMonthPivotMode.Funded:
                                return row.older.fundedAmount;
                            case DashboardOutstandingByMonthPivotMode.Number:
                                return row.older.number;
                            default:
                                return null;
                        }
                    }}
                    format={columnValueFormat}
                    alignment='left'
                    width={120}
                />

                <Summary
                    calculateCustomSummary={(options) => {
                        switch (options.name) {
                            case 'mtdSummary': {
                                if (options.summaryProcess === 'start') {
                                    options.totalValue = 0;
                                }
                                if (options.summaryProcess === 'calculate') {
                                    const { monthToDate } = options.value;

                                    switch (pivotMode) {
                                        case DashboardOutstandingByMonthPivotMode.Committed:
                                            options.totalValue += monthToDate.committedAmount;
                                            break;
                                        case DashboardOutstandingByMonthPivotMode.Funded:
                                            options.totalValue += monthToDate.fundedAmount;
                                            break;
                                        case DashboardOutstandingByMonthPivotMode.Number:
                                            options.totalValue += monthToDate.number;
                                            break;
                                    }
                                }
                                break;
                            }

                            case 'olderSummary': {
                                if (options.summaryProcess === 'start') {
                                    options.totalValue = 0;
                                }
                                if (options.summaryProcess === 'calculate') {
                                    const { older } = options.value;

                                    switch (pivotMode) {
                                        case DashboardOutstandingByMonthPivotMode.Committed:
                                            options.totalValue += older.committedAmount;
                                            break;
                                        case DashboardOutstandingByMonthPivotMode.Funded:
                                            options.totalValue += older.fundedAmount;
                                            break;
                                        case DashboardOutstandingByMonthPivotMode.Number:
                                            options.totalValue += older.number;
                                            break;
                                    }
                                }
                                break;
                            }
                        }
                    }}
                >
                    <TotalItem
                        column='loanTypeId'
                        summaryType='count'
                        displayFormat='Total'
                        showInColumn='loanTypeId'
                    />

                    <TotalItem
                        column='committedAmount'
                        summaryType='sum'
                        valueFormat='currency'
                        displayFormat='{0}'
                    />

                    <TotalItem
                        column='fundedAmount'
                        summaryType='sum'
                        valueFormat='currency'
                        displayFormat='{0}'
                    />
                    <TotalItem
                        column='number'
                        summaryType='sum'
                        displayFormat='{0}'
                    />
                    <TotalItem
                        name='mtdSummary'
                        summaryType='custom'
                        showInColumn='MTD'
                        displayFormat='{0}'
                        valueFormat={columnValueFormat}
                    />
                    {dataSource[0]?.monthlyDetails.map((m, idx) => (
                        <TotalItem
                            key={m.month}
                            summaryType='sum'
                            displayFormat='{0}'
                            valueFormat={columnValueFormat}
                            column={m.month}
                        />
                    ))}
                    <TotalItem
                        name='olderSummary'
                        summaryType='custom'
                        showInColumn='Older'
                        displayFormat='{0}'
                        valueFormat={columnValueFormat}
                    />
                </Summary>
            </TabularGrid>
        </div>
    );
};
